import React from 'react';
import { motion } from 'framer-motion';
import { BsClockHistory, BsChatDots, BsFolder, BsFlag, BsChevronRight } from 'react-icons/bs';
import { useNavigate } from 'react-router-dom';

interface ProjectsTabProps {
    projects: any[];
}

const ProjectsTab: React.FC<ProjectsTabProps> = ({ projects }) => {
    const navigate = useNavigate();

    const getDaysLeft = (dueDate: string) => {
        if (!dueDate) return null;
        const diffTime = new Date(dueDate).getTime() - new Date().getTime();
        return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    };

    if (projects.length === 0) {
        return (
            <div className="text-center py-20 bg-white/5 border border-white/10 rounded-xl">
                <h3 className="text-xl font-bold text-white mb-2">No Active Projects</h3>
                <p className="text-white/50 mb-6">You haven't hired any talent for a project yet.</p>
            </div>
        );
    }

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {projects.map((p, index) => (
                <motion.div
                    key={p.id || p._id || index}
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: index * 0.1 }}
                    className="group bg-white/5 border border-white/10 rounded-xl p-6 hover:border-premium-gold/40 hover:shadow-[0_0_30px_rgba(212,175,55,0.1)] transition-all cursor-pointer relative overflow-hidden"
                    onClick={() => navigate(`/project-workspace/${p.id || p._id}`)}
                >
                    {/* Hover Glow Background */}
                    <div className="absolute inset-0 bg-gradient-to-tr from-premium-gold/0 to-premium-gold/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"></div>

                    <div className="flex justify-between items-start mb-4 relative z-10">
                        <div>
                            <h3 className="text-xl font-bold text-white group-hover:text-premium-gold transition-colors">{p.name || 'Untitled Project'}</h3>
                            <div className="text-xs text-white/40 mt-1 flex items-center gap-2">
                                <span className={`px-2 py-0.5 rounded uppercase font-bold tracking-wider text-[10px] ${p.status === 'completed' ? 'bg-green-500/20 text-green-400' : p.status === 'delayed' ? 'bg-red-500/20 text-red-400' : 'bg-blue-500/20 text-blue-400'}`}>
                                    {p.status || 'Active'}
                                </span>
                                <span>•</span>
                                <span>{p.budget ? `$${p.budget.toLocaleString()} Budget` : 'No Budget Set'}</span>
                            </div>
                        </div>

                        {/* Talent Avatars Overlapping */}
                        <div className="flex -space-x-3">
                            {(p.members || []).slice(0, 3).map((member: any, i: number) => (
                                <div key={i} className="w-10 h-10 rounded-full border-2 border-[#1A1A1A] group-hover:border-premium-wood-dark transition-colors bg-white/10 flex items-center justify-center text-white/50 text-xs font-bold overflow-hidden">
                                    {member.user?.avatar ? <img src={member.user.avatar} alt="Talent" className="w-full h-full object-cover" /> : '?'}
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="mb-6 relative z-10">
                        <div className="flex justify-between text-xs mb-2">
                            <span className="text-white/60">Progress</span>
                            <span className="text-premium-gold font-bold">{p.progress || 0}%</span>
                        </div>
                        <div className="w-full bg-black/40 rounded-full h-2 overflow-hidden border border-white/5">
                            <div className="bg-gradient-to-r from-premium-gold-dark to-premium-gold-light h-full rounded-full transition-all duration-1000 relative" style={{ width: `${p.progress || 0}%` }}>
                                <div className="absolute top-0 right-0 bottom-0 left-0 bg-white/20 animate-pulse"></div>
                            </div>
                        </div>
                    </div>

                    <div className="flex items-center justify-between pt-4 border-t border-white/10 relative z-10">
                        <div className="flex items-center gap-4 text-white/40">
                            <button className="hover:text-premium-gold transition-colors flex items-center gap-1.5 text-xs" onClick={(e) => { e.stopPropagation(); navigate(`/project-workspace/${p.id || p._id}?tab=chat`) }}>
                                <BsChatDots className="w-4 h-4" /> <span className="hidden sm:inline">Chat</span>
                            </button>
                            <button className="hover:text-premium-gold transition-colors flex items-center gap-1.5 text-xs" onClick={(e) => { e.stopPropagation(); navigate(`/project-workspace/${p.id || p._id}?tab=files`) }}>
                                <BsFolder className="w-4 h-4" /> <span className="hidden sm:inline">Files</span>
                            </button>
                            <button className="hover:text-premium-gold transition-colors flex items-center gap-1.5 text-xs" onClick={(e) => { e.stopPropagation(); navigate(`/project-workspace/${p.id || p._id}?tab=milestones`) }}>
                                <BsFlag className="w-4 h-4" /> <span className="hidden sm:inline">Milestones</span>
                            </button>
                        </div>

                        {getDaysLeft(p.dueDate) !== null && (
                            <div className={`flex items-center gap-2 text-xs font-bold px-3 py-1.5 rounded-lg border ${getDaysLeft(p.dueDate)! > 0 ? 'text-orange-400 bg-orange-500/10 border-orange-500/20' : 'text-red-400 bg-red-500/10 border-red-500/20'}`}>
                                <BsClockHistory /> {getDaysLeft(p.dueDate)! > 0 ? `${getDaysLeft(p.dueDate)} Days Left` : 'Overdue'}
                            </div>
                        )}
                    </div>

                    <div className="absolute right-6 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 group-hover:translate-x-2 transition-all duration-300">
                        <BsChevronRight className="text-premium-gold opacity-50 w-6 h-6" />
                    </div>
                </motion.div>
            ))}
        </div>
    );
};

export default ProjectsTab;
